<template>
  <div style="display: content; height: 100%">
    <StickyFooter
      :sticky="!!selectedAssayType && !!templateId"
      :grow-content="!!selectedAssayType && !!templateId"
    >
      <template #header>
        <div class="hero is-info">
          <div class="loader-hero-body">
            <span class="loader-hero-title">Select Assay Type</span>
            <ul class="loader-step-checklist">
              <li class="mr-2" :class="{ 'step-done': assayType }">
                Select assay type
              </li>
              <li class="mr-2" :class="{ 'step-done': selectedTemplateId }">
                (optional) Select template
              </li>
            </ul>
            <span class="hero-add-data-link is-size-7">
              <a
                class="has-text-primary"
                :class="{ 'is-underlined': showAddDataHelp }"
                event
                @click="showAddDataHelp = !showAddDataHelp"
              >
                No, I want to add data to an existing assay
                <i
                  class="fa fa-xs pl-1"
                  :class="[
                    showAddDataHelp ? 'fa-chevron-down' : 'fa-chevron-right',
                  ]"
                />
              </a>
              <span v-if="showAddDataHelp" class="help m-0">
                To add data to an existing assay, go to the detail page of the
                assay and click the
                <code class="has-text-primary">add data</code>
                button
              </span>
            </span>
          </div>
        </div>
      </template>
      <template #body>
        <div class="padded-container assay-type-panels">
          <DefaultPanel
            v-for="catKey in visibleDomainCategoryKeys"
            :key="`section${catKey}`"
            :collapsed="false"
          >
            <template #header>
              <span class="icon mr-2">
                <i :class="domainCategories[catKey].iconClasses" />
              </span>
              {{ $filters.capitalizeWords(domainCategories[catKey].label) }}
              Assays
            </template>
            <div class="edge-to-edge">
              <ul class="assay-type-list">
                <li
                  v-for="assay in assaysPerCategory[catKey]"
                  :key="`seq-cat-${assay.type}`"
                  class="assay-type-item"
                  :class="{
                    'is-selected': isSelectedType(assay.type),
                  }"
                >
                  <label
                    :ref="`select-${assay.type}`"
                    :for="`select-${assay.type}`"
                    class="assay-type-label"
                    :class="{
                      'has-preview': isSelectedType(assay.type) && templateId,
                    }"
                  >
                    <input
                      :id="`select-${assay.type}`"
                      type="checkbox"
                      name="select-assay-type"
                      :value="assay.type"
                      :checked="isSelectedType(assay.type)"
                      style="display: none"
                      @change="selectedAssayType = assay.type"
                    />
                    <!-- Icon (grid column 1) -->
                    <span
                      class="assay-type-icon icon is-medium"
                      :class="{
                        'has-text-primary': isSelectedType(assay.type),
                      }"
                    >
                      <i
                        class="fa-lg"
                        :class="[
                          isSelectedType(assay.type)
                            ? 'fa-solid fa-check-circle'
                            : 'fa-regular fa-circle',
                        ]"
                      />
                    </span>
                    <!-- Name (grid column 2) -->
                    <span class="assay-type-name">
                      {{ assay.label }}
                    </span>

                    <!-- Controls (only when selected) -->
                    <div
                      v-if="isSelectedType(assay.type)"
                      class="assay-type-controls"
                    >
                      <div class="assay-type-controls__template">
                        <div class="select is-small">
                          <select
                            v-tooltip="
                              !assayTypeHasTemplate(assay.type)
                                ? 'No template available for this assay type'
                                : undefined
                            "
                            class="template-select"
                            :disabled="!assayTypeHasTemplate(assay.type)"
                            @change="
                              (event) => {
                                selectedTemplateId = event.target.value
                              }
                            "
                          >
                            <option
                              v-if="assayTypeHasTemplate(assay.type)"
                              :value="null"
                            >
                              Select template
                            </option>
                            <option v-else :value="null">No Template</option>
                            <option
                              v-for="id in templateByCategories[assay.type]"
                              :key="`template-opt-${id}`"
                              :value="id"
                              :selected="id === selectedTemplateId"
                            >
                              {{ templateById[id].name.value.name }}
                            </option>
                          </select>
                        </div>
                        <button
                          class="button is-small"
                          @click="
                            () => {
                              selectedType = assay.type
                              isOpenSelectAssayModal = true
                            }
                          "
                        >
                          <span>Select assay as template</span>
                        </button>
                        <span
                          class="icon is-small hoverable-icon has-text-primary"
                        >
                          <i
                            v-tooltip="
                              `A template - or an existing an assay - can be
                                selected to create a new similar assay. The new
                                assay will be initialised with certain values
                                obtained from the template.`
                            "
                            class="fa fa-question-circle fa-xs"
                          />
                        </span>
                      </div>
                      <div
                        v-if="!templateId"
                        class="assay-type-controls__continue"
                      >
                        <span class="help is-size-7">or</span>
                        <router-link
                          v-tooltip="
                            `A new ${assayType} assay will be created from scratch`
                          "
                          :to="$attrs.nextRoute"
                        >
                          <BaseButton
                            variant="primary"
                            size="small"
                            :outlined="true"
                            :disabled="!canContinue"
                            :icon="['fa', 'fa-step-forward']"
                            label="Continue without template"
                          />
                        </router-link>
                      </div>
                    </div>
                    <template v-if="isSelectedType(assay.type)">
                      <div
                        v-if="selectedTemplateId && templateId"
                        class="assay-preview-container"
                      >
                        <p class="assay-preview-header">
                          <span>Template preview</span>
                          <a
                            class="has-text-primary"
                            @click="resetTemplateData"
                          >
                            <span class="icon is-small">
                              <i class="fa-solid fa-times" />
                            </span>
                          </a>
                        </p>
                        <AssayView
                          class="assay-preview"
                          :dv="templatePreviewDv"
                        />
                      </div>
                    </template>
                  </label>
                </li>
              </ul>
            </div>
          </DefaultPanel>
        </div>
      </template>
      <template v-if="!!selectedAssayType && !!templateId" #footer>
        <p v-if="templateId" class="mb-2">
          The new assay will be initialised with some values of
          <a
            v-tooltip="`${templateName} [${templateType}]`"
            class="has-text-primary"
          >
            the selected template
          </a>
        </p>
        <router-link :to="$attrs.nextRoute" class="mt-3 mb-3">
          <BaseButton
            v-tooltip="
              !canContinue
                ? 'You need to select an assay type to continue'
                : undefined
            "
            variant="primary"
            :outlined="!canContinue"
            :disabled="!canContinue"
            :icon="['fa', 'fa-step-forward']"
            label="Continue with template"
          />
        </router-link>
      </template>
    </StickyFooter>
    <ModalCard
      v-if="isOpenSelectAssayModal"
      :title="`
        <span class='icon is-small mr-1'>
          <i class='fa-solid fa-search-plus fa-xs'></i>
        </span>
        <span>Search for ${selectedAssayLabel} assays</span>
      `"
      :handle-close="() => (isOpenSelectAssayModal = false)"
      width="95%"
    >
      <template #body>
        <ListComponent
          :key="`list${selectedType}`"
          app-name="assays"
          model-name="assays"
          :model-type="selectedType.toLowerCase()"
          vuex-prefix="assaytypeselect-"
          :is-compact="true"
          :show-add-controls="false"
          :show-global-search="true"
          :show-owner-controls="true"
          :can-save-settings="false"
          init-ownership="all"
          @selected="(selected) => (selectedAssays = selected)"
        />
      </template>
      <template #footer>
        <div>
          <div class="is-flex is-align-items-center is-flex-wrap-wrap">
            <span v-if="selectedAssays.length > 1" class="has-text-danger pr-4">
              Please pick a single assay
            </span>
            <button
              :disabled="
                selectedAssays.length === 0 || selectedAssays.length > 1
              "
              class="button is-primary is-outlined is-small"
              @click="
                () => {
                  selectedTemplateId = selectedAssays[0].id.value
                  isOpenSelectAssayModal = false
                }
              "
            >
              <span>Confirm</span>
            </button>
          </div>
        </div>
      </template>
    </ModalCard>
  </div>
</template>

<script>
import { computed, provide } from 'vue'
import { mapActions, mapMutations, mapState } from 'vuex'

import { get } from '@/api'
import { useAssayDomains } from '@/composables/useAssayDomains'
import { useRecordPreview } from '@/composables/useRecordPreview'
import { useStore } from '@/store'
import { scrollToElement } from '@/utils'
import { resolveModelMetaFromId } from '@/utils/api'

import StickyFooter from '@/components/data-loader/StickyFooter.vue'
import DefaultPanel from '@/components/DefaultPanel.vue'
import AssayView from '@/components/detail/assay/AssayView.vue'

export default {
  name: 'AssayTypeSelection',
  emits: ['can-continue'],
  components: {
    AssayView,
    DefaultPanel,
    StickyFooter,
  },
  setup() {
    provide('linkBehavior', 'blank')
    const store = useStore()
    const templateType = computed(
      () => store.state.dataloader.assay.templateData?.type?.value?.name,
    )
    const templateData = computed(
      () => store.state.dataloader.assay.templateData,
    )
    const templatePreviewDv = useRecordPreview({
      appName: 'assays',
      modelName: 'assays',
      modelType: templateType,
      record: templateData,
    })

    const rawAssayMenuModels = computed(
      () => store.getters['meta/menuCategories']?.assays?.modelTypes || [],
    )
    const assayMenuModels = computed(() =>
      rawAssayMenuModels.value
        .filter(
          (ass) =>
            // InstrumentRun is a menu navigation item, not a selectable assay type
            ass.urlPath[1] !== 'instrumentrun' &&
            // 'total' is an aggregate/count type used for menu display ("All"), not a real assay type
            ass.modelType !== 'total',
        )
        .map((ass) => ({
          ...ass,
          type: ass.modelType,
          label: ass.modelTypeLabel,
        })),
    )

    const { domainCategories, assaysPerDomain, visibleDomainKeys } =
      useAssayDomains(assayMenuModels)

    return {
      templatePreviewDv,
      assayMenuModels,
      domainCategories,
      assaysPerCategory: assaysPerDomain,
      visibleDomainCategoryKeys: visibleDomainKeys,
    }
  },
  data() {
    return {
      templates: [],
      selectedAssays: [],
      selectedType: null,
      showAddDataHelp: false,
      isOpenSelectAssayModal: false,
    }
  },
  computed: {
    ...mapState('dataloader/assay', {
      assayType: (_, getters) => getters.resolvedAssayType,
      templateId: (state) => state.templateData?.id?.value,
      templateName: (state) => state.templateData?.name?.value?.name,
      templateType: (state) => state.templateData?.type?.value?.name,
    }),
    selectedAssayType: {
      get() {
        return this.assayType?.toLowerCase()
      },
      set(value) {
        this.resetAssayData()
        this.resetTemplateData()
        this.setAssayType(value)
      },
    },
    selectedAssayLabel() {
      return (
        this.assayMenuModels.find((a) => this.selectedAssayType === a.modelType)
          ?.modelTypeLabel || ''
      )
    },
    selectedTemplateId: {
      get() {
        return this.templateId
      },
      set(id) {
        this.setTemplateData(id)
      },
    },
    templateByCategories() {
      return this.templates.reduce((all, elem) => {
        const modelType = elem?.type?.value?.name?.toLowerCase()
        const categoryTemplates = all[modelType] || []
        categoryTemplates.push(elem.id.value)
        return {
          ...all,
          [modelType]: categoryTemplates,
        }
      }, {})
    },
    templateById() {
      return this.templates.reduce(
        (all, elem) => ({
          ...all,
          [elem.id.value]: elem,
        }),
        {},
      )
    },
    canContinue() {
      return !!this.selectedAssayType
    },
  },
  methods: {
    ...mapMutations('dataloader/assay', {
      resetAssayData: 'RESET_ASSAY_DATA',
      resetTemplateData: 'RESET_TEMPLATE_DATA',
    }),
    ...mapActions('dataloader/assay', ['setTemplateData', 'setAssayType']),
    async getTemplates() {
      this.loadingTemplates = true
      const templatesUrl = 'assays/assays/?is_template=true'
      const response = await get(templatesUrl)
      this.templates = response?.data?.results || []
      this.loadingTemplates = false
    },
    isSelectedType(assayType) {
      return this.selectedAssayType && this.selectedAssayType === assayType
    },
    assayTypeHasTemplate(assayType) {
      return this.templateByCategories[assayType]?.length > 0 || false
    },
  },
  watch: {
    templateType(t) {
      const selectAssayElem = this.$refs[`select-${this.selectedAssayType}`][0]
      if (t)
        this.$nextTick(() =>
          scrollToElement(selectAssayElem, { block: 'start' }),
        )
    },
    canContinue(cc) {
      this.$emit('can-continue', cc)
    },
  },
  async beforeMount() {
    this.getTemplates()
    if (this.$route.query.templateId) {
      const templateId = this.$route.query.templateId
      // const meta = false
      try {
        await resolveModelMetaFromId(templateId)
        this.selectedTemplateId = templateId
      } catch (e) {
        const error = JSON.parse(e?.request?.response || '')
        console.error('Unknown template ID: ', error?.message)
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.hero-add-data-link {
  margin-left: auto;
  text-align: right;

  @include mobile {
    text-align: left;
    margin-left: 0;
  }
}

.assay-type-panels {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-block: var(--space-4);
}

.assay-type-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.assay-type-label {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: var(--space-2);
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  padding: var(--space-3);
  scroll-margin: var(--space-3);
  cursor: pointer;

  &:hover {
    background-color: var(--color-background-default);
    border-color: var(--color-border-default);
  }

  .is-selected & {
    background-color: var(--brand-light);
    border-color: var(--color-border-default);
  }

  &.has-preview {
    padding: var(--space-4);
  }
}

.assay-type-icon {
  grid-column: 1;
  grid-row: 1;
}

.assay-type-name {
  grid-column: 2;
  grid-row: 1;
}

/* Controls: sits in column 2, perfectly aligned with the name */
.assay-type-controls {
  grid-column: 2;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding-top: var(--space-2);
}

.assay-type-controls__template {
  display: contents;
}

.assay-type-controls__continue {
  display: contents;
}

.template-select {
  width: 200px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @include mobile {
    width: 100%;
    max-width: 200px;
  }
}

/* ── Template preview ── */
.assay-preview-container {
  grid-column: 1 / -1;
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background-color: var(--color-background-default);
  margin-top: var(--space-3);
}

.assay-preview-header {
  display: flex;
  justify-content: space-between;
  padding: var(--space-2);
}

.assay-preview {
  max-height: 500px;
  overflow: auto;
  border: 1px solid var(--color-border-default);
  background: var(--color-background-surface, #fff);
  margin-bottom: var(--space-1);

  :deep(.panel-heading) {
    padding: var(--space-2);
  }

  :deep(.panel) {
    box-shadow: none;
  }

  :deep(td) {
    padding: var(--space-1) 0;
  }
}
</style>
